import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ActualitesService } from '../actualites.service';
import { PermissionService } from '../../../../core/service/permission/permission.service';

export interface Actualite {
  news_id: number;
  news_name: string;
  news_excerpt: string;
  news_content: string;
  news_author?: string;
  news_begin_date: string;
  news_end_date?: string;
  news_status: 'ACTI' | 'INAC';
  news_status_label?: string;
  news_tags?: string;
  file_link?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

@Component({
  selector: 'app-actualites-list',
  standalone: false,
  templateUrl: './actualites-list.component.html',
  styleUrls: ['./actualites-list.component.scss']
})
export class ActualitesListComponent implements OnInit, OnDestroy {
  readonly permService = inject(PermissionService);
  actualites = signal<Actualite[]>([]);
  isLoading = signal(true);
  isFiltering = signal(false);
  errorMessage = signal<string | null>(null);
  currentPage = signal(1);
  searchQuery = signal('');
  selectedStatus = signal('');
  totalActualites = signal(0);
  perPage = signal(8);
  pagination = signal<Pagination>({ page: 1, limit: 8, total: 0, totalPages: 0 });

  pageNumbers = computed((): (number | '...')[] => {
    const { page, totalPages } = this.pagination();
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  });

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private actualitesService: ActualitesService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(query => {
      this.searchQuery.set(query);
      this.loadActualites(1, true);
    });
    this.loadActualites(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  private loadActualites(page: number, filtering = false): void {
    filtering ? this.isFiltering.set(true) : this.isLoading.set(true);
    this.errorMessage.set(null);

    this.actualitesService.getActualites(page, this.pagination().limit, this.searchQuery(), this.selectedStatus())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.actualites.set(response.data || []);
          const total = response.pagination?.total || response.total || 0;
          this.pagination.set({
            page,
            limit: this.pagination().limit,
            total,
            totalPages: Math.ceil(total / this.pagination().limit),
          });
          this.isLoading.set(false);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors du chargement des actualités');
          this.isLoading.set(false);
          this.isFiltering.set(false);
        }
      });
  }

  onSearch(value: string): void {
    this.searchSubject.next(value);
  }

  onStatusFilter(value: string): void {
    this.selectedStatus.set(value);
    this.loadActualites(1, true);
  }

  onPageChange(page: number): void {
    const { totalPages } = this.pagination();
    if (page < 1 || page > totalPages) return;
    this.loadActualites(page);
  }

  onAddNew(): void {
    this.router.navigate(['/federation/actualites/new']);
  }

  onView(id: number): void {
    this.router.navigate(['/federation/actualites', id]);
  }

  onEdit(id: number): void {
    this.router.navigate(['/federation/actualites', id, 'edit']);
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const parent = img.parentElement;
    if (parent) {
      parent.innerHTML = '<svg class="w-5 h-5 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>';
    }
  }

  // ── Status confirmation modal ───────────────────────────────────────────




  statusLabel(status: string): string {
    const map: Record<string, string> = { ACTI: 'En ligne', INAC: 'Hors ligne' };
    return map[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

}
